/**
 * Dictionnaire de libellés AGORA (6 langues). Règle : aucun texte en dur dans le code –
 * toute nouvelle clé est ajoutée dans les 6 fichiers (vérifié par `npm run i18n:check` en CI).
 * Interpolation : {nom}. Repli : langue demandée → français → clé.
 *
 * Clés TYPÉES : `traduire(langue, cle)` n'accepte que les clés existant dans fr.json (CleTraduction) – une clé
 * inconnue ou mal orthographiée est une erreur de compilation. Clé construite à l'exécution (code d'erreur, id du
 * catalogue, colonne…) : `cleLibre(prefixe, suffixe)`, limité aux préfixes dynamiques déclarés ci-dessous.
 */
import fr from './fr.json';
import en from './en.json';
import de from './de.json';
import it from './it.json';
import es from './es.json';
import pt from './pt.json';
import { LANGUE_PAR_DEFAUT, type Langue } from '../langues';

type Arbre = { [cle: string]: string | Arbre };

/** Toutes les clés (chemins pointés jusqu'aux feuilles) du dictionnaire source (français). */
type Feuilles<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Feuilles<T[K], `${P}${K}.`>;
}[keyof T & string];
export type CleTraduction = Feuilles<typeof fr>;

/** Préfixes sous lesquels une clé peut être construite à l'exécution (à compléter avec parcimonie). */
export const PREFIXES_DYNAMIQUES = [
  'catalogue', 'erreurs', 'langues', 'pays', 'droits', 'connexion.erreurs', 'accueil.widgets', 'accueil.indicateurs',
  'dim.portee', 'dim.tiers.colonnes', 'dim.tiers.types', 'dim.tiers.visibilite', 'dim.filiales.colonnes', 'pj.types',
  'notifications.types',
  'dim.contacts.colonnes', 'dim.import.champs', 'dim.import.erreurs',
  'commerce.solutions.colonnes', 'commerce.solutions.carac', 'commerce.tarifs.colonnes', 'commerce.inari.erreurs',
  'commerce.import.champs', 'commerce.import.erreurs', 'commerce.affaires.champs', 'commerce.affaires.colonnes', 'commerce.affaires.evenements',
  'it.incidents.evenements', 'it.incidents.historique.champs',
  'formulaires.erreurs',
] as const;
export type PrefixeDynamique = (typeof PREFIXES_DYNAMIQUES)[number];

export const DICTIONNAIRE: Record<Langue, Arbre> = { fr, en, de, it, es, pt };

/** Recherche tolérante aux clés contenant des points (ids du catalogue : catalogue.service.gmao). */
function chercher(arbre: Arbre, cle: string): string | undefined {
  const direct = arbre[cle];
  if (typeof direct === 'string') return direct;
  const i = cle.indexOf('.');
  if (i < 0) return undefined;
  const sous = arbre[cle.slice(0, i)];
  return sous && typeof sous !== 'string' ? chercher(sous, cle.slice(i + 1)) : undefined;
}

/**
 * Clé construite à l'exécution sous un préfixe dynamique déclaré (ex. cleLibre('erreurs', probleme.code)).
 * Une clé absente retombe sur la clé elle-même à l'affichage (repli de traduire) : le contrôle i18n et les tests
 * vérifient la complétude des familles connues (codes d'erreur, catalogue…).
 */
export function cleLibre(prefixe: PrefixeDynamique, suffixe: string): CleTraduction {
  return `${prefixe}.${suffixe}` as CleTraduction;
}

/** Vrai si la clé existe dans le dictionnaire source. */
export function cleExiste(cle: string): boolean {
  return chercher(DICTIONNAIRE[LANGUE_PAR_DEFAUT], cle) !== undefined;
}

/** Libellé d'un nœud du catalogue (métier, module, sous-module, dimension). */
export function libelle(langue: Langue, id: string): string {
  return traduire(langue, cleLibre('catalogue', id));
}

export function traduire(langue: Langue, cle: CleTraduction, variables?: Record<string, string | number>): string {
  const brut = chercher(DICTIONNAIRE[langue], cle) ?? chercher(DICTIONNAIRE[LANGUE_PAR_DEFAUT], cle) ?? cle;
  if (!variables) return brut;
  return brut.replace(/\{(\w+)\}/g, (m, nom: string) => (nom in variables ? String(variables[nom]) : m));
}

/** Liste à plat de toutes les clés d'un arbre (utilisé par le contrôle de complétude). */
export function cles(arbre: Arbre, prefixe = ''): string[] {
  return Object.entries(arbre).flatMap(([k, v]) => (typeof v === 'string' ? [prefixe + k] : cles(v, `${prefixe}${k}.`)));
}
